import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'

import PageSectionHeader from '../../../features/components/PageSectionHeader'
import FacebookAdsPreviewList from './FacebookAdsPreviewList'
import TikTokAdsPreviewList from './TikTokAdsPreviewList'
import AdsGallery from './AdsGallery'
import AdsTable from './AdsTable'
import Can from '../../../features/components/Can'
import AdTikTokEdit from '../../../forms/Tiktok/AdForms/AdTikTokEdit'
import AdGoogleEdit from '../../../forms/Google/AdForms/AdGoogleEdit'
import AdDv360Edit from '../../../forms/DV360/AdForms/AdDv360Edit'
import AdsChangeHistory from './AdsChangeHistory'

import useAdsFetchData from './hooks/useAdsFetchData'

import { getSelectedPlatform } from '../../../helpers/handlePlatformSelection'
import { useDeleteAdAction } from '../../../features/hooks/useDeleteAction/useDeleteAdAction'

import { clearAds, clearUpdateAd } from '../../../modules/actions/ads'
import {
  adWasUpdatedSelector,
  adUpdateErrorSelector,
  adsSelector,
  adsAfterSelector
} from '../../../modules/selectors/ads'
import { selectedAdAccountsSelector } from '../../../modules/selectors/adAccounts'

import {
  DV_360_PLATFORM,
  FACEBOOK_PLATFORM,
  GOOGLE_PLATFORM,
  TIKTOK_PLATFORM
} from '../../../constants/selectLists/platformList'

function AdsList({ isLineItemSummaryPage, scrollRef, RightPanel }) {
  const dispatch = useDispatch()

  const { campaign_id: campaignId, line_item_id: lineItemId } = useParams()

  const ads = useSelector(adsSelector)
  const selectedPlatform = getSelectedPlatform()
  const adWasUpdated = useSelector(adWasUpdatedSelector)
  const adUpdateError = useSelector(adUpdateErrorSelector)
  const adsAfter = useSelector(adsAfterSelector)
  const { [selectedPlatform]: selectedAdAccount = {} } = useSelector(selectedAdAccountsSelector)

  const { id: selectedAdAccountId } = selectedAdAccount

  const getAdsData = useAdsFetchData({
    adAccountId: selectedAdAccountId,
    campaignId,
    lineItemId,
    platform: selectedPlatform,
    withPagination: selectedPlatform === FACEBOOK_PLATFORM || selectedPlatform === DV_360_PLATFORM
  })

  const loadMoreAdsHandler = useCallback(() => {
    getAdsData(adsAfter)
  }, [getAdsData, adsAfter])

  const { handleDeleteRequest, DeleteAdModal } = useDeleteAdAction()

  const AdsListComponent = useMemo(() => {
    switch (selectedPlatform) {
      case FACEBOOK_PLATFORM:
        return (
          <FacebookAdsPreviewList
            adsList={ads}
            isLineItemSummaryPage={isLineItemSummaryPage}
            loadMoreAdsHandler={loadMoreAdsHandler}
            noDataText="There are no ads in this campaign."
            handleDeleteRequest={handleDeleteRequest}
          />
        )
      case DV_360_PLATFORM:
        return <AdsGallery adsList={ads} loadMoreAdsHandler={loadMoreAdsHandler} />
      case TIKTOK_PLATFORM:
        return <TikTokAdsPreviewList adsList={ads} />
      case GOOGLE_PLATFORM:
      default:
        return <AdsTable />
    }
  }, [ads, isLineItemSummaryPage, loadMoreAdsHandler, selectedPlatform, handleDeleteRequest])

  useEffect(() => {
    getAdsData && getAdsData()
  }, [getAdsData])

  useEffect(() => {
    if (adWasUpdated || adUpdateError) {
      dispatch(clearUpdateAd())
    }
  }, [dispatch, adWasUpdated, adUpdateError])

  useEffect(
    () => () => {
      dispatch(clearAds())
    },
    [dispatch]
  )

  return (
    <>
      <section id="ad-cards-list" ref={scrollRef}>
        <PageSectionHeader title="List of Ads" RightPanel={RightPanel} />

        {AdsListComponent}
        {DeleteAdModal}
      </section>

      {selectedPlatform === GOOGLE_PLATFORM && (
        <Can I="update" a="Ad">
          <AdGoogleEdit />
        </Can>
      )}
      {selectedPlatform === DV_360_PLATFORM && (
        <Can I="update" a="Ad">
          <AdDv360Edit />
        </Can>
      )}
      {selectedPlatform === TIKTOK_PLATFORM && (
        <Can I="update" a="Ad">
          <AdTikTokEdit />
        </Can>
      )}
      <AdsChangeHistory platform={selectedPlatform} />
    </>
  )
}

export default AdsList
